// @flow

import React, {useState, useRef} from "react";
import {Col, Modal, Row} from "antd";
import {
    RedoOutlined,
    UploadOutlined,
    DeleteOutlined,
    ZoomOutOutlined,
    ZoomInOutlined,
    RotateLeftOutlined,
    RotateRightOutlined
} from "@ant-design/icons";
import ReactAvatarEditor from "react-avatar-editor";
import {useTranslation} from "react-i18next";

import {SliderField, InputField} from "./imageEditorStyles";
import userPlaceholder from "../../../public/images/person-placeholder.jpg";
import companyPlaceholder from "../../../public/images/company-placeholder.jpg";
import {imageEditorPropType} from "./ImageEditorPropTypes";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {useStore} from "../../../hooks/useStore";
import config from "../../../_core/config";

export const ImageEditorModal = ({
    visible,
    setVisible,
    setStatePhoto,
    setPhoto,
    isCompany,
    logoImg,
    setPhotoLogo,
    companyPhotoId
}: imageEditorPropType) => {
    const {t} = useTranslation(["imageEditorModal"]);
    const placeholder = isCompany ? companyPlaceholder : userPlaceholder;
    const fileInput = useRef();
    const editor = useRef();
    const [scale, setScale] = useState(1);
    const [rotate, setRotate] = useState(0);
    const [position, setPosition] = useState({x: 0.5, y: 0.5});
    const [img, setImg] = useState(placeholder);
    const stores = useStore();

    const resetSettings = () => {
        setScale(1);
        setRotate(0);
        setPosition({x: 0.5, y: 0.5});
    };

    return (
        <Modal
            cancelText={t("modal.cancelButton2", {ns: "common"})}
            okText={t("modal.saveButton", {ns: "common"})}
            onCancel={() => {
                resetSettings();
                setVisible(false);
            }}
            onOk={async () => {
                const image = await editor.current.getImageScaledToCanvas();
                await image.toBlob(async blob => {
                    await setPhoto(image.toDataURL());
                    await setStatePhoto(image);
                    await setVisible(false);
                });
            }}
            title={t("modal.editPhotoTitle", {ns: "imageEditorModal"})}
            visible={visible}
            data-testid="photoEditorModal"
            okButtonProps={{"data-testid": "photoEditorModal_submitButton"}}
            cancelButtonProps={{"data-testid": "photoEditorModal_cancelButton"}}
        >
            <Row type="flex" justify="center" gutter={[0, 20]}>
                <ReactAvatarEditor
                    ref={editor}
                    scale={scale}
                    width={isCompany ? 450 : 250}
                    height={isCompany ? 150 : 250}
                    position={position}
                    onPositionChange={p => setPosition(p)}
                    rotate={rotate}
                    borderRadius={0}
                    image={img !== placeholder ? img : `${config.awsUrl}${companyPhotoId}`}
                    data-testid="courierAvatarEditor"
                />
                <InputField
                    ref={fileInput}
                    data-testid="courierAvatarEditorInput"
                    accept=".png,.jpg,.jpeg,.heic,.hevc,.tiff,.gif,.pdf"
                    onChange={event => {
                        setImg(event.target.files[0]);
                        setPhotoLogo(event.target.files[0]);
                        event.target.value = "";
                    }}
                    type="file"
                />
            </Row>
            <Row type="flex" justify="center" align="middle">
                <CustomButton
                    data-testid="profilePhotoAddChangeButton"
                    onClick={() => fileInput.current.click()}
                    icon={<UploadOutlined />}
                >
                    {img !== placeholder
                        ? t("actions.changePhoto", {ns: "imageEditorModal"})
                        : t("actions.addPhoto", {ns: "imageEditorModal"})}
                </CustomButton>
            </Row>
            <Row type="flex" justify="center" align="middle">
                <Col span={1}>
                    <ZoomOutOutlined
                        data-testid="profilePhotoZoomOutButton"
                        onClick={() => {
                            if (scale > 0.7) setScale(scale - 0.1);
                        }}
                    />
                </Col>
                <Col span={12}>
                    <SliderField
                        tooltipVisible={false}
                        min={0.7}
                        max={3}
                        step={0.1}
                        onChange={value => setScale(value)}
                        value={scale}
                        data-testid="profilePhotoZoomSlider"
                    />
                </Col>
                <Col span={1}>
                    <ZoomInOutlined
                        data-testid="profilePhotoZoomInButton"
                        onClick={() => {
                            if (scale < 3) setScale(scale + 0.1);
                        }}
                    />
                </Col>
            </Row>
            <Row type="flex" justify="center" align="middle">
                <Col span={1}>
                    <RotateLeftOutlined
                        data-testid="profilePhotoRotateLeftButton"
                        onClick={() => {
                            if (rotate > -180) setRotate(rotate - 5);
                        }}
                    />
                </Col>
                <Col span={12}>
                    <SliderField
                        tooltipVisible={false}
                        min={-180}
                        max={180}
                        step={5}
                        onChange={value => setRotate(value)}
                        value={rotate}
                        data-testid="profilePhotoRotateSlider"
                    />
                </Col>
                <Col span={1}>
                    <RotateRightOutlined
                        data-testid="profilePhotoRotateRightButton"
                        onClick={() => {
                            if (rotate < 180) setRotate(rotate + 5);
                        }}
                    />
                </Col>
            </Row>
            <Row type="flex" justify="center" align="middle">
                {img !== placeholder && (
                    <CustomButton
                        data-testid="deleteButton"
                        disabled={img === placeholder}
                        onClick={() => {
                            setImg(placeholder);
                            resetSettings();
                        }}
                        icon={<DeleteOutlined />}
                    >
                        {t("actions.delete", {ns: "common"})}
                    </CustomButton>
                )}
                <CustomButton
                    type="link"
                    data-testid="resetSettings"
                    onClick={() => resetSettings()}
                    disabled={
                        scale === 1 && rotate === 0 && position.x === 0.5 && position.y === 0.5
                    }
                    icon={<RedoOutlined />}
                >
                    {t("actions.resetSettings", {ns: "imageEditorModal"})}
                </CustomButton>
            </Row>
        </Modal>
    );
};
